export type SheetSnap = 'collapsed' | 'medium' | 'large';

export const SHEET_COLLAPSED_PX = 120;
export const SHEET_MEDIUM_RATIO = 0.5;
export const SHEET_LARGE_RATIO = 0.85;

/** Above this speed (px/ms) a release is a flick, not a position pick. */
const FLICK_VELOCITY = 0.5;

const SNAP_ORDER: readonly SheetSnap[] = ['collapsed', 'medium', 'large'];

/** `mediumRatio`: share of the body at the medium height (default half). */
export function getSnapHeights(
  bodyHeight: number,
  mediumRatio = SHEET_MEDIUM_RATIO
): Record<SheetSnap, number> {
  return {
    collapsed: SHEET_COLLAPSED_PX,
    medium: Math.round(bodyHeight * mediumRatio),
    large: Math.round(bodyHeight * SHEET_LARGE_RATIO),
  };
}

/**
 * Snap to settle on after a drag. `velocityPxPerMs` > 0 means the panel is
 * growing (finger moving up), < 0 shrinking.
 */
export function nearestSnap(
  heightPx: number,
  bodyHeight: number,
  velocityPxPerMs = 0,
  mediumRatio = SHEET_MEDIUM_RATIO
): SheetSnap {
  const heights = getSnapHeights(bodyHeight, mediumRatio);

  if (velocityPxPerMs > FLICK_VELOCITY) {
    return SNAP_ORDER.find((snap) => heights[snap] > heightPx) ?? 'large';
  }
  if (velocityPxPerMs < -FLICK_VELOCITY) {
    return (
      [...SNAP_ORDER].reverse().find((snap) => heights[snap] < heightPx) ??
      'collapsed'
    );
  }

  return SNAP_ORDER.reduce((best, snap) =>
    Math.abs(heights[snap] - heightPx) < Math.abs(heights[best] - heightPx)
      ? snap
      : best
  );
}

export function nextSnapOnHandleTap(current: SheetSnap): SheetSnap {
  return SNAP_ORDER[(SNAP_ORDER.indexOf(current) + 1) % SNAP_ORDER.length];
}

export function stepSnap(
  current: SheetSnap,
  direction: 'up' | 'down'
): SheetSnap {
  const index = SNAP_ORDER.indexOf(current) + (direction === 'up' ? 1 : -1);
  return SNAP_ORDER[Math.min(SNAP_ORDER.length - 1, Math.max(0, index))];
}
